<script>
	import { onMount, tick } from "svelte";

	import CellC from "./Cell.svelte";
	import PieceC from "./Piece.svelte";
	import PromoteChoiceC from "./PromoteChoice.svelte";

	import { crate } from "../../../wasm.js";
	const { Color, PieceKind, Piece } = crate;

	export let engine;
	export let size = 400 + 4;

	$: cellSize = (size - 4) / 8;

	let root;
	let topLeft = { x: 0, y: 0 };

	let moveSound = new Audio("/assets/sounds/move.webm");
	let captureSound = new Audio("/assets/sounds/capture.webm");
	let checkSound = new Audio("/assets/sounds/check.webm");
	let gameEndSound = new Audio("/assets/sounds/game-end.webm");
	let illegalSound = new Audio("/assets/sounds/illegal.webm");

	illegalSound.volume = 0.25;

	document.body.appendChild(moveSound);
	document.body.appendChild(captureSound);

	function computeTopLeft() {
		const rect = root.getBoundingClientRect();
		topLeft = {
			x: rect.x + rect.width / (cellSize * 4),
			y: rect.y + rect.height / (cellSize * 4),
		};
	}

	let observer;
	onMount(() => {
		computeTopLeft();

		observer = new ResizeObserver((entries) => {
			computeTopLeft();
		});
		observer.observe(root);

		return function () {
			observer.disconnect();
		};
	});

	$: cells = Array.from(engine.cells()).map(Piece.from_encoded);

	$: isInCheck = engine.is_in_check();
	$: legalMoves = engine.legal_moves();
	$: colorToMove = engine.color_to_move();

	$: oppositeColor = colorToMove === Color.White ? Color.Black : Color.White;

	$: triagedLegalMoves = (() => {
		const init = {};
		for (let i = 0; i < 64; i++) {
			init[i] = [];
		}
		return legalMoves.reduce((acc, it) => {
			const from = it.from.y * 8 + it.from.x;
			const to = it.to.y * 8 + it.to.x;
			it.to.idx = to;
			it.from.idx = from;
			acc[from].push(it);
			return acc;
		}, init);
	})();

	let hover = undefined;
	let canMove = [];
	let lastMove = undefined;
	let promotionChoices = undefined;
	let choiceX = 0;
	let choiceY = 0;

	$: legalMovesCount = Object.values(triagedLegalMoves).reduce(
		(acc, it) => acc + it.length,
		0
	);
	$: gameEnded = (() => {
		if (legalMovesCount === 0) {
			return { kind: "Checkmate", color: oppositeColor };
		}
		return undefined;
	})();

	function playSound(move) {
		if (gameEnded) {
			gameEndSound.play();
			return;
		}
		if (isInCheck) {
			checkSound.play();
			return;
		}
		if (move.capture !== null) {
			captureSound.play();
			return;
		}
		moveSound.play();
	}

	function submitMove(move) {
		console.log(
			`Move from (${move.from.x}, ${move.from.y}) to (${move.to.x}, ${move.to.y})`
		);

		if (engine.submit_move(move)) {
			lastMove = {
				from: move.from.y * 8 + move.from.x,
				to: move.to.y * 8 + move.to.x,
			};

			//? Refresh the UI to reflect new state.
			engine = engine;

			tick().then(() => {
				playSound(move);
			});
		}

		canMove = [];
		hover = undefined;
		promotionChoices = undefined;
	}

	function handleMoveStart(piece, idx) {
		return function (event) {
			if (piece.kind === PieceKind.None) {
				return;
			}

			hover = {
				idx,
				piece,
				pos: { x: event.pageX - topLeft.x, y: event.pageY - topLeft.y },
				from: { x: Math.trunc(idx % 8), y: Math.trunc(idx / 8) },
			};
			canMove = triagedLegalMoves[idx];
		};
	}

	function handleMoveStop(piece, idx) {
		return function (event) {
			if (hover === undefined) {
				return;
			}

			const { from } = hover;
			const to = { x: Math.trunc(idx % 8), y: Math.trunc(idx / 8) };

			const moves = canMove.filter(
				(it) =>
					it.from.x === from.x &&
					it.from.y === from.y &&
					it.to.x === to.x &&
					it.to.y === to.y
			);

			// The move is found to be illegal.
			if (moves.length === 0) {
				hover = undefined;
				canMove = [];
				promotionChoices = undefined;
				illegalSound.play();
				return;
			}

			// Multiple moves for the same piece displacement.
			// This can only be in the case of a promotion.
			if (moves.length > 1) {
				promotionChoices = moves.filter((it) => it.promotion);
				choiceX = cellSize * to.x;
				// prettier-ignore
				choiceY = cellSize * (to.y - Math.max(0, to.y + promotionChoices.length - 8));
				hover = undefined;
				canMove = [];
				return;
			}

			submitMove(moves[0]);
		};
	}

	function handleMouseMove(event) {
		if (hover) {
			hover.pos.x = event.pageX - topLeft.x;
			hover.pos.y = event.pageY - topLeft.y;
		}
	}

	function handlePromoteChoice(event) {
		const { option: move } = event.detail;
		submitMove(move);
	}
</script>

<div class="root" bind:this={root}>
	<div
		class="board"
		style={`--cell-size: ${cellSize}px`}
		on:mousemove={handleMouseMove}
	>
		{#each cells as piece, idx (idx)}
			<CellC
				x={Math.trunc(idx % 8)}
				y={Math.trunc(idx / 8)}
				{piece}
				to={lastMove && idx === lastMove.to}
				from={lastMove && idx === lastMove.from}
				move={canMove.find((it) => idx === it.to.idx)}
				dim={hover && idx === hover.idx}
				check={isInCheck &&
					piece.kind === PieceKind.King &&
					piece.color === colorToMove}
				on:pointerdown={handleMoveStart(piece, idx)}
				on:pointerup={handleMoveStop(piece, idx)}
			/>
		{/each}
	</div>
	{#if hover}
		<div
			class="hover"
			style={`
			--pos-x: ${hover.pos.x}px;
			--pos-y: ${hover.pos.y}px;
			--cell-size: ${cellSize}px;
		`}
		>
			<PieceC color={hover.color} piece={hover.piece} />
		</div>
	{/if}
	{#if promotionChoices}
		<div class="overlay" />
		<PromoteChoiceC
			x={choiceX}
			y={choiceY}
			{cellSize}
			options={promotionChoices}
			on:choice={handlePromoteChoice}
		/>
	{/if}
</div>

<style>
	.root {
		position: relative;
	}

	.board {
		display: grid;
		grid-template-rows: repeat(8, var(--cell-size));
		grid-template-columns: repeat(8, var(--cell-size));
		align-items: center;
		justify-content: center;
		overflow: hidden;
	}

	.hover {
		position: absolute;
		top: var(--pos-y);
		left: var(--pos-x);
		width: var(--cell-size);
		height: var(--cell-size);
		transform: translate(-50%, -50%);
		pointer-events: none;
	}

	.overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}
</style>
